/* -------------------------------------------------------------------------- */
/*  src/pages/Dashboard.jsx – Update 16                                       */
/*  ▸ Jobs, Users UND Tasks parallel geladen                                  */
/*  ▸ Neuer Abschnitt mit drei Cards zwischen Chart und Grid                  */
/*  ▸ Erste Card: Donut Chart für Task-Verteilung (basierend auf TODOXX)      */
/*  ▸ Fix: Adjust customLegendLabel to use props.value and props.percentage;  */
/*         Set legend text color to match segment color                       */
/* -------------------------------------------------------------------------- */
import * as React from 'react';

import { ButtonGroup, Button }   from '@progress/kendo-react-buttons';
import { DateRangePicker }       from '@progress/kendo-react-dateinputs';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { useLocalization }       from '@progress/kendo-react-intl';

import { Grid as DataGrid, Column } from './../components/Grid'; // Umbenannt, um Konflikt mit Kendo Grid zu vermeiden
import { Chart as LineColumnChart } from './../components/Chart'; // Umbenannt für Klarheit
import { Chart, ChartLegend, ChartSeries, ChartSeriesItem, ChartTitle, ChartCategoryAxis, ChartCategoryAxisItem } from '@progress/kendo-react-charts';
import { ColumnMenu }            from '@progress/kendo-react-grid';
import { Loader }                from '@progress/kendo-react-indicators';

import '@progress/kendo-theme-default/dist/all.css'; // Import des Themes

import config    from './../config/config';

/* Hilfsfunktionen --------------------------------------------------------- */
const monthStart = d => new Date(d.getFullYear(), d.getMonth(), 1);
const nextMonth  = d => new Date(d.getFullYear(), d.getMonth() + 1, 1);
const palette = [
  '#0277BD', '#2E7D32', '#EF6C00', '#AD1457',
  '#6A1B9A', '#00838F', '#9E9D24', '#5D4037'
];

/* ---------- Custom-Cells -------------------------------------------------- */
const NameCell = ({ dataItem }) => (
  <td style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    {dataItem.avatar
      ? <img src={dataItem.avatar} alt=""
             style={{ width: 32, height: 32, borderRadius: '50%' }} />
      : <span style={{ width: 32, height: 32, borderRadius: '50%',
                       background: '#ccc' }} />
    }
    <span>{dataItem.fullName}</span>
  </td>
);

const ActiveCell = ({ dataItem }) => (
  <td>
    <span style={{
      display: 'inline-block',
      width: 14, height: 14, borderRadius: 3,
      background: dataItem.isActive ? '#2E7D32' : '#D32F2F'
    }} />
  </td>
);

const EmployeeTypeCell = ({ dataItem }) => (
  <td>{dataItem.externalEmployee
        ? 'Externer Mitarbeiter'
        : 'Exploserv Mitarbeiter'}
  </td>
);

/* -------------------------------------------------------------------------- */
const Dashboard = () => {
  const loc = useLocalization();

  /* -------------------- State ------------------------------------------ */
  const [jobs,    setJobs]    = React.useState([]);
  const [tasks,   setTasks]   = React.useState([]); // Tasks für Referenz (Beschreibungen/Einheiten), könnte Array oder Object sein
  const [empRows, setEmpRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isTrend, setIsTrend] = React.useState(true);
  const [range,   setRange]   = React.useState({ start: null, end: null });

  /* -------------------- Daten laden ------------------------------------ */
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        /* Jobs + Users + Tasks parallel holen */
        const [jobsRes, usersRes, tasksRes] = await Promise.all([
          fetch(`${config.host}/jobs/expanded`),
          fetch(`${config.host}/users`),
          fetch(`${config.host}/tasks`) // Tasks laden für Referenz
        ]);
        const [jobsRaw, usersRaw, tasksRaw] = await Promise.all([
          jobsRes.json(),
          usersRes.json(),
          tasksRes.json()
        ]);

        /* ----- Users für Grid aufbereiten ------------------------------ */
        const usersMapped = usersRaw.map(u => ({
          employeeId:       u.BenutzerID,
          fullName:         `${u.Vorname} ${u.Nachname}`.trim() ||
                            u.Benutzername || 'Unbekannt',
          avatar:           u.AvatarThumbLink
            ? `${config.host}/get_image/${encodeURIComponent(u.AvatarThumbLink)}`
            : '',
          isActive:         String(u.Active).toLowerCase() === 'true',
          email:            u.EmailAdresse ?? '',
          externalEmployee: !!u.ExternalEmployee
        }));
        setEmpRows(usersMapped);

        /* ----- Jobs für Chart vorbereiten ------------------------------ */
        const jobsEnriched = jobsRaw.map(j => ({
          ...j,
          date: j.AusfuehrungszeitStart
            ? new Date(j.AusfuehrungszeitStart)
            : null
        }));
        setJobs(jobsEnriched);

        /* ----- Tasks speichern ----------------------------------------- */
        setTasks(tasksRaw); // Könnte Array oder Object sein

        /* Range einmalig setzen ---------------------------------------- */
        if (jobsEnriched.length) {
          const dates = jobsEnriched
            .filter(j => j.date)
            .map(j => monthStart(j.date))
            .sort((a, b) => a - b);
          setRange({
            start: dates[0],
            end:   nextMonth(dates[dates.length - 1])
          });
        }
      } catch (err) {
        console.error('Fetch fehlgeschlagen:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* -------------------- Chart-Daten (bestehender Chart) ---------------- */
  const chartData = React.useMemo(() => {
    const items  = [];
    const resMap = new Map();

    jobs.forEach(j => {
      if (!j.date) return;
      const cat = monthStart(j.date);

      (j.MitarbeiterArray || []).forEach(emp => {
        const id = emp.id || emp.name;
        if (!resMap.has(id)) {
          resMap.set(id, {
            employeeId:   id,
            employeeName: emp.name,
            employeeColor: palette[resMap.size % palette.length]
          });
        }
        items.push({ employeeId: id, orderDate: cat, jobCount: 1 });
      });
    });

    return { items, resources: Array.from(resMap.values()) };
  }, [jobs]);

  /* -------------------- Donut Chart-Daten (angepasst für TODOXX) ------- */
  const pieData = React.useMemo(() => {
    if (!jobs.length) return [];

    // Filter Jobs nach Range
    const filteredJobs = jobs.filter(j => 
      j.date && j.date >= (range.start ?? new Date('1970-01-01')) && j.date < (range.end ?? new Date('3000-01-01'))
    );

    // Map für Einheiten aus Tasks (handle Array oder Object)
    let einheitenMap = new Map();
    if (Array.isArray(tasks)) {
      einheitenMap = new Map(tasks.map(t => [t.Beschreibung.toLowerCase(), t.Einheit]));
    } else if (typeof tasks === 'object' && tasks !== null) {
      // Annahme: tasks ist {Task01: {Beschreibung, Einheit}, ...}
      einheitenMap = new Map(
        Object.values(tasks)
          .filter(val => val && val.Beschreibung && val.Einheit)
          .map(val => [val.Beschreibung.toLowerCase(), val.Einheit])
      );
    }
    // Falls tasks undefined oder anderes, einheitenMap bleibt empty

    // Aggregation: Summe Aufwand pro Beschreibung
    const sums = new Map();

    filteredJobs.forEach(j => {
      // Durch alle Felder des Jobs iterieren und TODOXX finden
      Object.keys(j).forEach(key => {
        if (key.startsWith('TODO') && /^\d+$/.test(key.slice(4))) { // z.B. TODO01, TODO02, ...
          const todo = j[key];
          if (todo && todo.Beschreibung && todo.Aufwand) {
            const beschreibung = todo.Beschreibung.trim();
            const aufwand = parseFloat(todo.Aufwand) || 0;
            if (aufwand > 0) {
              sums.set(beschreibung, (sums.get(beschreibung) || 0) + aufwand);
            }
          }
        }
      });
    });

    // Add colors from palette
    return Array.from(sums.entries()).map(([category, value], index) => ({
      category,
      value,
      einheit: einheitenMap.get(category.toLowerCase()) || '', // Optional für Labels
      color: palette[index % palette.length] // Add color for each segment
    })).filter(d => d.value > 0);
  }, [jobs, tasks, range]);

  const customLegendLabel = (props) => {
    const einheit = props.pointIndex !== undefined && pieData[props.pointIndex] ? pieData[props.pointIndex].einheit ? ` (${props.value} ${pieData[props.pointIndex].einheit})` : '' : '';
    const percent = props.percentage.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 });
    return `${props.text}${einheit}: ${percent}`;
  };

  /* -------------------- Area Chart-Daten für Card 2 -------------------- */
  const areaData = React.useMemo(() => {
    if (!jobs.length) return { categories: [], series: [] };

    // Filter Jobs nach Range
    const filteredJobs = jobs.filter(j => 
      j.date && j.date >= (range.start ?? new Date('1970-01-01')) && j.date < (range.end ?? new Date('3000-01-01'))
    );

    // Map für Einheiten aus Tasks (handle Array oder Object)
    let einheitenMap = new Map();
    if (Array.isArray(tasks)) {
      einheitenMap = new Map(tasks.map(t => [t.Beschreibung.toLowerCase(), t.Einheit]));
    } else if (typeof tasks === 'object' && tasks !== null) {
      einheitenMap = new Map(
        Object.values(tasks)
          .filter(val => val && val.Beschreibung && val.Einheit)
          .map(val => [val.Beschreibung.toLowerCase(), val.Einheit])
      );
    }

    // Unique Monate sammeln (als 'YYYY-MM')
    const monthsSet = new Set();
    filteredJobs.forEach(j => {
      if (j.date) {
        const monthKey = `${j.date.getFullYear()}-${String(j.date.getMonth() + 1).padStart(2, '0')}`;
        monthsSet.add(monthKey);
      }
    });
    const categories = Array.from(monthsSet).sort(); // Sortierte Monate

    // Unique Tasks sammeln
    const tasksSet = new Set();
    filteredJobs.forEach(j => {
      Object.keys(j).forEach(key => {
        if (key.startsWith('TODO') && /^\d+$/.test(key.slice(4))) {
          const todo = j[key];
          if (todo && todo.Beschreibung) {
            tasksSet.add(todo.Beschreibung.trim());
          }
        }
      });
    });
    const uniqueTasks = Array.from(tasksSet);

    // Für jede Task eine Serie erstellen
    const series = uniqueTasks.map((task, index) => {
      const data = categories.map(monthKey => {
        let sum = 0;
        filteredJobs.forEach(j => {
          if (j.date && `${j.date.getFullYear()}-${String(j.date.getMonth() + 1).padStart(2, '0')}` === monthKey) {
            Object.keys(j).forEach(key => {
              if (key.startsWith('TODO') && /^\d+$/.test(key.slice(4))) {
                const todo = j[key];
                if (todo && todo.Beschreibung.trim() === task && todo.Aufwand) {
                  sum += parseFloat(todo.Aufwand) || 0;
                }
              }
            });
          }
        });
        return sum;
      });
      return {
        name: task,
        data,
        color: palette[index % palette.length]
      };
    });

    return { categories, series };
  }, [jobs, tasks, range]);

  /* -------------------- Handler ---------------------------------------- */
  const onRangeChange = ({ value }) => setRange(value);

  const onStartChange = (e) =>
  setRange(prev => ({ ...prev, start: e.value }));

  const onEndChange = (e) =>
  setRange(prev => ({ ...prev, end: e.value }));

  const toTrend  = () => setIsTrend(true);
  const toVolume = () => setIsTrend(false);

  const chartStart = range.start ?? new Date('1970-01-01');
  const chartEnd   = range.end   ?? new Date('3000-01-01');

  /* -------------------- Render ----------------------------------------- */
  return (
    <div id="Dashboard" className="dashboard-page main-content">
      {/* ================= Bestehender Chart ============================ */}
      <div className="card-container grid">
        <h3 className="card-title">
          {loc.toLanguageString('custom.teamEfficiency')}
        </h3>

        <div className="card-buttons">
          <ButtonGroup>
            <Button togglable selected={isTrend}  onClick={toTrend}>
              {loc.toLanguageString('custom.trend')}
            </Button>
            <Button togglable selected={!isTrend} onClick={toVolume}>
              {loc.toLanguageString('custom.volume')}
            </Button>
          </ButtonGroup>
        </div>

        <div className="card-ranges"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label htmlFor="start-date">Start:</label>
          <DatePicker
            id="start-date"
            value={range.start}
            onChange={onStartChange}
            format="dd.MM.yyyy"
          />

          <label htmlFor="end-date" style={{ marginLeft: 12 }}>Ende:</label>
          <DatePicker
            id="end-date"
            value={range.end}
            onChange={onEndChange}
            format="dd.MM.yyyy"
          />
        </div>

        <div className="card-component">
          {loading ? (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', marginTop: 80
            }}>
              <Loader size="large" type="infinite-spinner" />
              <p>Daten werden geladen…</p>
            </div>
          ) : (
            <LineColumnChart
              data={chartData.items}
              filterStart={chartStart}
              filterEnd={chartEnd}
              groupByField="employeeId"
              groupResourceData={chartData.resources}
              groupTextField="employeeName"
              groupColorField="employeeColor"
              seriesCategoryField="orderDate"
              seriesField="jobCount"
              seriesType={isTrend ? 'line' : 'column'}
              onRefresh={null}
            />
          )}
        </div>
      </div>

      {/* ================= Neuer Abschnitt: Drei Cards ================== */}
      <div className="cards-row" style={{ display: 'flex', gap: '16px', margin: '16px 0' }}>
        {/* ----- Erste Card: Donut Chart für Tasks ----- */}
        <div className="card-container" style={{ flex: 1, minWidth: '500px' }}>
          <h3 className="card-title">
            Task-Verteilung
          </h3>

          <div className="card-component">
            {loading ? (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', marginTop: 40
              }}>
                <Loader size="large" type="infinite-spinner" />
                <p>Daten werden geladen…</p>
              </div>
            ) : pieData.length > 0 ? (
              <Chart style={{ height: 400 }}>
                <ChartLegend 
                  visible={true} 
                  position="right" 
                  orientation="vertical" 
                  labels={{ 
                    content: customLegendLabel,
                    color: (props) => pieData[props.pointIndex]?.color || 'black' // Textfarbe passend zum Segment
                  }} 
                />
                <ChartSeries>
                  <ChartSeriesItem
                    type="donut"
                    data={pieData}
                    field="value"
                    categoryField="category"
                    colorField="color"
                    holeSize={50} // Größe des inneren Lochs für Donut-Effekt
                    labels={{ visible: false }} // Labels im Chart entfernen
                  />
                </ChartSeries>
              </Chart>
            ) : (
              <p>Keine Task-Daten verfügbar.</p>
            )}
          </div>
        </div>

        {/* ----- Zweite Card: Area Chart für Task Aufwand pro Monat ----- */}
        <div className="card-container" style={{ flex: 1, minWidth: '500px' }}>
          <h3 className="card-title" style={{ marginBottom: '25px' }}>
            Task Aufwand pro Monat
          </h3>

          <div className="card-component">
            {loading ? (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', marginTop: 40
              }}>
                <Loader size="large" type="infinite-spinner" />
                <p>Daten werden geladen…</p>
              </div>
            ) : areaData.categories.length > 0 ? (
              <Chart style={{ height: 400 }}>
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem categories={areaData.categories} title={{ text: 'Monate' }} />
                </ChartCategoryAxis>
                <ChartSeries>
                  {areaData.series.map((serie, index) => (
                    <ChartSeriesItem
                      key={index}
                      type="area"
                      data={serie.data}
                      color={serie.color}
                      name={serie.name}
                    />
                  ))}
                </ChartSeries>
                <ChartLegend visible={true} position="bottom" />
              </Chart>
            ) : (
              <p>Keine Daten verfügbar.</p>
            )}
          </div>
        </div>


      </div>

      {/* ================= Grid ========================================= */}
      <div className="card-container grid">
        <h3 className="card-title">
          {loc.toLanguageString('custom.teamMembers')}
        </h3>

        <span />

        <div className="card-component">
          <DataGrid
            data={empRows}
            style={{ height: 450 }}
            selectable={false}
            onDataChange={setEmpRows}
          >
            <Column field="fullName" title="Name"   width={250} cell={NameCell} />
            <Column field="isActive" title="Aktiv"  width={100} cell={ActiveCell} />
            <Column field="email"    title="E-Mail" width={230} />
            <Column field="externalEmployee" title="Typ"
                    width={200} cell={EmployeeTypeCell} />
          </DataGrid>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;